import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { money } from "../calc";
import { generateEventPDF, generateEventContractPDF } from "../pdf";

const EVENT_STATUS = [
  { v: "agendado", l: "Agendado" },
  { v: "realizado", l: "Realizado" },
  { v: "cancelado", l: "Cancelado" },
];
const COST_FIELDS = [
  { k: "team", l: "Equipe" },
  { k: "displacement", l: "Deslocamento base→local" },
  { k: "food", l: "Alimentação" },
  { k: "lodging", l: "Hospedagem" },
  { k: "other", l: "Outros" },
];

const emptyForm = () => ({
  name: "", location: "", date: "", endDate: "", revenue: "",
  costs: { team: "", displacement: "", food: "", lodging: "", other: "" },
  vehicleName: "", team: "", status: "agendado", notes: "",
});

const totalCost = (costs = {}) => COST_FIELDS.reduce((s, f) => s + Number(costs[f.k] || 0), 0);

export default function EventosTab({ settings }) {
  const [events, setEvents] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = () => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm()); setEditingId(null); setShowForm(true); };
  const openEdit = (ev) => {
    setForm({
      name: ev.name || "", location: ev.location || "", date: ev.date || "", endDate: ev.endDate || "",
      revenue: ev.revenue ?? "", costs: { ...emptyForm().costs, ...(ev.costs || {}) },
      vehicleName: ev.vehicleName || "", team: ev.team || "", status: ev.status || "agendado", notes: ev.notes || "",
    });
    setEditingId(ev.id); setShowForm(true);
  };
  const duplicate = (ev) => {
    setForm({
      name: ev.name || "", location: ev.location || "", date: "", endDate: "",
      revenue: ev.revenue ?? "", costs: { ...emptyForm().costs, ...(ev.costs || {}) },
      vehicleName: ev.vehicleName || "", team: ev.team || "", status: "agendado", notes: ev.notes || "",
    });
    setEditingId(null); setShowForm(true);
    toast.info("Evento duplicado — informe a nova data e salve.");
  };
  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setCost = (k) => (e) => setForm({ ...form, costs: { ...form.costs, [k]: e.target.value } });

  const save = async () => {
    if (!form.name.trim()) return toast.error("Informe o nome do evento.");
    if (!form.date) return toast.error("Informe a data do evento.");
    setLoading(true);
    const payload = {
      name: form.name.trim(), location: form.location.trim(), date: form.date, endDate: form.endDate || "",
      revenue: Number(form.revenue || 0),
      costs: Object.fromEntries(COST_FIELDS.map((f) => [f.k, Number(form.costs[f.k] || 0)])),
      vehicleName: form.vehicleName, team: form.team, status: form.status, notes: form.notes,
    };
    try {
      if (editingId) await api.patch(`/events/${editingId}`, payload);
      else await api.post("/events", payload);
      toast.success(editingId ? "Evento atualizado." : "Evento cadastrado.");
      setShowForm(false); setEditingId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const setStatus = async (ev, status) => {
    try { await api.patch(`/events/${ev.id}`, { status }); toast.success(`Evento marcado como ${status}.`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (ev) => {
    if (!window.confirm(`Excluir o evento "${ev.name}"?`)) return;
    try { await api.delete(`/events/${ev.id}`); toast.success("Evento excluído."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const pill = (s) => ({ agendado: { bg: "#e3f0fb", fg: "#0b477d" }, realizado: { bg: "#e7f7ee", fg: "#0a6f3c" }, cancelado: { bg: "#fdeaea", fg: "#b53b3b" } }[s] || { bg: "#eef2f6", fg: "#516b82" });
  const fmtDate = (d) => (d ? new Date(d + "T12:00:00").toLocaleDateString("pt-BR") : "—");
  const ft = totalCost(form.costs);
  const fProfit = Number(form.revenue || 0) - ft;

  return (
    <section className="panel active">
      <div className="module-head">
        <div><h1>Eventos</h1><p>Cobertura de ambulância em festas, shows e eventos. Some ao faturamento mensal quando marcado como realizado.</p></div>
        <button className="primary" onClick={openNew} data-testid="event-new">＋ Novo evento</button>
      </div>

      {showForm && (
        <article className="card" data-testid="event-form">
          <h2>{editingId ? "Editar evento" : "Novo evento"}</h2>
          <div className="module-grid two-col">
            <label className="full-label">Nome do evento<input value={form.name} onChange={setF("name")} data-testid="event-name" placeholder="Ex.: Show na Praça / Festa Corporativa" /></label>
            <label className="full-label">Local / Endereço<input value={form.location} onChange={setF("location")} data-testid="event-location" placeholder="Endereço do evento" /></label>
            <label className="full-label">Data<input type="date" value={form.date} onChange={setF("date")} data-testid="event-date" /></label>
            <label className="full-label">Data fim (opcional)<input type="date" value={form.endDate} onChange={setF("endDate")} data-testid="event-enddate" /></label>
            <label className="full-label">Valor recebido (R$)<input type="number" step="0.01" value={form.revenue} onChange={setF("revenue")} data-testid="event-revenue" placeholder="0,00" /></label>
            <label className="full-label">Ambulância / Veículo
              <select value={form.vehicleName} onChange={setF("vehicleName")} data-testid="event-vehicle">
                <option value="">Selecione</option>
                {vehicles.map((v) => <option key={v.id} value={v.name || v.plate || v.model}>{v.name || v.model || v.plate}</option>)}
              </select>
            </label>
            <label className="full-label">Equipe (separe por vírgula)<input value={form.team} onChange={setF("team")} data-testid="event-team" placeholder="Ex.: Enfermeiro João, Socorrista Maria" /></label>
            <label className="full-label">Status
              <select value={form.status} onChange={setF("status")} data-testid="event-status">
                {EVENT_STATUS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </label>
          </div>
          <h3 style={{ margin: "10px 0 6px", color: "#0b477d", fontSize: 14 }}>Custos</h3>
          <div className="module-grid" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))" }}>
            {COST_FIELDS.map((f) => (
              <label className="full-label" key={f.k}>{f.l} (R$)
                <input type="number" step="0.01" value={form.costs[f.k]} onChange={setCost(f.k)} data-testid={`event-cost-${f.k}`} placeholder="0,00" />
              </label>
            ))}
          </div>
          <label className="full-label">Observações<textarea value={form.notes} onChange={setF("notes")} data-testid="event-notes" rows={2} placeholder="A ambulância permanece no local, saindo apenas em urgência/emergência." /></label>
          <div className="final-price" style={{ marginTop: 10 }}>
            <span>Custo total: <strong>{money(ft)}</strong> • Lucro estimado: <strong style={{ color: fProfit >= 0 ? "#0a6f3c" : "#b53b3b" }}>{money(fProfit)}</strong></span>
          </div>
          <div style={{ marginTop: 12 }}>
            <button className="primary" onClick={save} disabled={loading} data-testid="event-save">{loading ? "Salvando…" : "Salvar evento"}</button>
            <button className="secondary" style={{ marginLeft: 8 }} onClick={() => { setShowForm(false); setEditingId(null); }} data-testid="event-cancel">Cancelar</button>
          </div>
        </article>
      )}

      <article className="card" style={{ marginTop: 16 }}>
        <h2>Eventos cadastrados</h2>
        {events.length === 0 ? (
          <p className="hint">Nenhum evento cadastrado ainda. Clique em "Novo evento" para começar.</p>
        ) : (
          <div className="module-list">
            {events.map((ev) => {
              const tc = totalCost(ev.costs);
              const profit = Number(ev.revenue || 0) - tc;
              return (
                <div className="list-card" key={ev.id} data-testid={`event-${ev.id}`}>
                  <div>
                    <strong>{ev.name}</strong>
                    <span className="status-pill" style={{ background: pill(ev.status).bg, color: pill(ev.status).fg, marginLeft: 8 }} data-testid={`event-status-${ev.id}`}>{EVENT_STATUS.find((s) => s.v === ev.status)?.l || ev.status}</span>
                    <span>{fmtDate(ev.date)}{ev.endDate ? ` – ${fmtDate(ev.endDate)}` : ""} • {ev.location || "Local não informado"}</span>
                    <small>Recebido {money(ev.revenue)} • Custo {money(tc)} • Lucro {money(profit)}{ev.vehicleName ? ` • ${ev.vehicleName}` : ""}</small>
                  </div>
                  <div className="list-value" style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {ev.status !== "realizado" && <button onClick={() => setStatus(ev, "realizado")} data-testid={`event-mark-done-${ev.id}`}>Marcar realizado</button>}
                    <button onClick={() => openEdit(ev)} data-testid={`event-edit-${ev.id}`}>Editar</button>
                    <button onClick={() => duplicate(ev)} data-testid={`event-duplicate-${ev.id}`}>Duplicar</button>
                    <button onClick={() => generateEventContractPDF(ev, settings)} data-testid={`event-contract-${ev.id}`}>Contrato</button>
                    <button onClick={() => generateEventPDF(ev, settings)} data-testid={`event-pdf-${ev.id}`}>PDF</button>
                    <button onClick={() => remove(ev)} data-testid={`event-delete-${ev.id}`} style={{ color: "#b53b3b" }}>Excluir</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </article>
    </section>
  );
}
